import type { ClassId } from "../classes/classes.ts";
import { contrast, hexToRgb } from "./color-math.ts";
import type { GlyphId } from "./glyphs.ts";

/**
 * Paleta fixa da Fase 8 (doc 09, §2): a única fonte dos hex de gameplay. O neon é linguagem, não
 * enfeite: a cor diz **quem** causou (chefe, inimigo ou a classe do jogador), a forma diz o quê e o
 * brilho diz quando. O resto do mundo é escala de cinza (`GRAYS`).
 */
export type Hex = `#${string}`;

/** Um ator com cor própria e o reforço que não depende de cor (daltonismo, §2.6). */
export interface ActorColor {
  readonly hex: Hex;
  /** Nome curto para legendas e opções. */
  readonly name: string;
  /** Glifo que acompanha a cor em toda parte (barra, seta fora da tela, telegrafia). */
  readonly glyph: GlyphId;
}

export const PALETTE = {
  /** Tudo que tem barra no topo da tela: chefes, Guardiões e o Dragão. */
  chefe: { hex: "#ff2929", name: "Chefe", glyph: "coroa" },
  /** Monstros da noite e fauna hostil. */
  inimigo: { hex: "#ff8a23", name: "Inimigos", glyph: "presa" },
  /**
   * A cor pertence à classe (F85, ADR 0017): uma por classe, a mesma em toda parte (personagem, anel,
   * HUD, mapa, efeitos e o acento da UI). Quem repete a classe divide a cor e se distingue pelo número
   * do jogador (o hexágono com o `PlayerInfo.slot` + 1) e pelo nome.
   */
  classes: {
    baluarte: { hex: "#00d9ff", name: "Baluarte", glyph: "hexagono" },
    lanceiro: { hex: "#12e68c", name: "Lanceiro", glyph: "hexagono" },
    arcanista: { hex: "#ff40b8", name: "Arcanista", glyph: "hexagono" },
    patrulheiro: { hex: "#3366ff", name: "Patrulheiro", glyph: "hexagono" },
  },
  /** Não é acento: interagíveis, perigos neutros e a UI fora da partida. */
  neutro: { hex: "#e8eef2", name: "Neutro", glyph: "nenhum" },
  /**
   * O brilho dos baús de saque (Fase 15): dourado claro e pouco saturado, para não se confundir com o
   * laranja dos inimigos nem com o amarelo deles no modo daltônico. Não é ator: ninguém causou.
   */
  tesouro: { hex: "#ffdf9a", name: "Tesouro", glyph: "nenhum" },
} as const satisfies {
  readonly chefe: ActorColor;
  readonly inimigo: ActorColor;
  readonly classes: Readonly<Record<ClassId, ActorColor>>;
  readonly neutro: ActorColor;
  readonly tesouro: ActorColor;
};

/** A cor da classe `id`; desconhecida (ou vazia), a do Baluarte (o mesmo padrão do `classOf`). */
export function classColor(id: string): Hex {
  return (PALETTE.classes as Readonly<Record<string, ActorColor | undefined>>)[id]?.hex ?? PALETTE.classes.baluarte.hex;
}

/**
 * Rampa de cinza do guia (§7), em fração de branco: o mundo ganha riqueza por separação de valor,
 * não por cor. Os papéis visuais (`visual-roles.ts`) escolhem faixas dentro dela.
 */
export const GRAYS = {
  abismo: 0.05,
  preto: 0.1,
  grafite: 0.2,
  escuro: 0.3,
  medio: 0.45,
  prata: 0.62,
  claro: 0.78,
  branco: 0.92,
} as const;

/** Fundos da UI e do céu: cinza muito escuro em vez de preto puro (guia, §3). */
export const BACKDROPS = {
  fundo: "#050505",
  painel: "#0d0d0d",
  elevado: "#121212",
} as const satisfies Record<string, Hex>;

/** Tom de neon de uma criatura (doc 09, §1.3 e §2): quem ela é para o jogador. */
export type CreatureTone = "chefe" | "inimigo" | "neutro";

/**
 * Chefes, Guardiões e o Dragão são vermelhos (têm barra no topo); os monstros, laranja; a fauna,
 * neutra, até atacar (o Javali encurralado acende em laranja: `hostile`).
 */
export function creatureTone(archetype: "neutra" | "assombracao" | "chefe" | "guardiao" | "final", hostile = false): CreatureTone {
  if (archetype === "chefe" || archetype === "guardiao" || archetype === "final") return "chefe";
  if (archetype === "assombracao" || hostile) return "inimigo";
  return "neutro";
}

/** Hex de cada tom. */
export const TONE_COLORS: Readonly<Record<CreatureTone, Hex>> = { chefe: PALETTE.chefe.hex, inimigo: PALETTE.inimigo.hex, neutro: PALETTE.neutro.hex };

/**
 * Variante da paleta para o modo daltônico (opcional; o dono autorizou em 2026-10-07, doc 09 §9): a
 * mesma ordem de matiz por classe (ciano, verde, rosa, azul) e chefe vermelho × inimigo amarelo, escolhida
 * por busca para afastar todos os pares nas quatro visões (normal, protan, deutan, tritan): ΔE00
 * mínimo 18,5, contra 6,2 da paleta fixa. Os glifos e padrões continuam valendo nos dois modos.
 */
export const COLORBLIND_PALETTE: Readonly<Record<string, Hex>> = {
  [PALETTE.chefe.hex]: "#e60000",
  [PALETTE.inimigo.hex]: "#ffc640",
  [PALETTE.classes.baluarte.hex]: "#56aabf",
  [PALETTE.classes.lanceiro.hex]: "#73ffc7",
  [PALETTE.classes.arcanista.hex]: "#bf5680",
  [PALETTE.classes.patrulheiro.hex]: "#7467e6",
};

/** A cor de jogo `hex` no modo pedido (a da paleta fixa, ou a do modo daltônico; o resto não muda). */
export function paletteColor(hex: string, colorblind: boolean): string {
  return colorblind ? (COLORBLIND_PALETTE[hex.toLowerCase()] ?? hex) : hex;
}

/** Contraste mínimo de texto pequeno (WCAG 1.4.3, AA). */
export const TEXT_CONTRAST = 4.5;

/**
 * A variante legível de um acento (F85): o próprio `hex` quando já passa de 4,5:1 como texto sobre o
 * painel **e** sob a tinta escura (texto sobre um preenchimento do acento); senão, o primeiro passo em
 * direção ao neutro que passa nos dois. Borda, brilho e anel de foco usam o acento puro (3:1 basta, e
 * todas as classes passam); texto e preenchimento com texto usam esta (o `--acento-claro`).
 */
export function readableAccent(hex: string, bg: string = BACKDROPS.painel, ink: string = BACKDROPS.fundo): Hex {
  // Só `#rrggbb` (as cores de jogo); outra coisa passa como veio.
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex as Hex;
  const passes = (c: string) => contrast(c, bg) >= TEXT_CONTRAST && contrast(c, ink) >= TEXT_CONTRAST;
  if (passes(hex)) return hex.toLowerCase() as Hex;
  const from = hexToRgb(hex);
  const to = hexToRgb(PALETTE.neutro.hex);
  for (let step = 1; step <= 100; step++) {
    const t = step / 100;
    const mixed = `#${from.map((c, i) => Math.round((c + (to[i]! - c) * t) * 255).toString(16).padStart(2, "0")).join("")}` as Hex;
    if (passes(mixed)) return mixed;
  }
  return PALETTE.neutro.hex;
}
